import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useSupabase, useTenant } from "@how/app-kit";
import { buildObjectPath, STORAGE_BUCKETS } from "@how/storage";
import { publicMediaUrl } from "@how/website-core";
import { env } from "../../lib/env";

function useTenantId(): string | null {
  const tenant = useTenant();
  return tenant.status === "resolved" ? tenant.tenant.tenantId : null;
}

/** What a church may upload. The bucket itself also caps the size and the types (ADR 0021). */
export const ACCEPTED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp", "image/avif"];
export const MAX_IMAGE_BYTES = 10 * 1024 * 1024;

/** The public address of one of the church's images. */
export function mediaUrl(path: string): string {
  return publicMediaUrl(env.VITE_SUPABASE_URL, path);
}

/**
 * A file name the storage policies accept: letters, digits, dot, dash and underscore only, and never a name that
 * collides with a file already there. The church's own name for the file survives in the alt text, not the path.
 */
export function storageFileName(original: string): string {
  const dot = original.lastIndexOf(".");
  const stem = (dot > 0 ? original.slice(0, dot) : original).toLowerCase();
  const extension = (dot > 0 ? original.slice(dot + 1) : "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
  const cleaned = stem
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);
  const unique = crypto.randomUUID().slice(0, 8);
  const base = cleaned.length > 0 ? `${cleaned}-${unique}` : unique;
  return extension.length > 0 ? `${base}.${extension}` : base;
}

/** The pixel size of an image, so a page can reserve its space and not jump while it loads. */
async function imageSize(file: File): Promise<{ width: number; height: number } | null> {
  try {
    const bitmap = await createImageBitmap(file);
    const size = { width: bitmap.width, height: bitmap.height };
    bitmap.close();
    return size;
  } catch {
    // A file the browser cannot decode is still uploaded; the page simply cannot reserve its space.
    return null;
  }
}

const failed = (error: { message: string; code?: string } | null) => {
  if (error !== null) throw Object.assign(new Error(error.message), { code: error.code });
};

function useAssetInvalidation() {
  const client = useQueryClient();
  const tenantId = useTenantId();
  return async () => {
    await client.invalidateQueries({ queryKey: ["assets", tenantId] });
  };
}

export interface UploadInput {
  readonly file: File;
  /** Required: what the image shows, for a reader who cannot see it. */
  readonly altText: string;
}

/**
 * Uploads the file to the church's media area, then records it. The two steps are deliberate: the storage policies
 * decide who may write the file (`media.manage`), and `register_asset` checks the same permission again before the
 * catalogue row exists. A file that uploads but fails to register is removed again rather than left orphaned.
 */
export function useUploadAsset() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  const invalidate = useAssetInvalidation();
  return useMutation({
    mutationFn: async (input: UploadInput): Promise<string> => {
      const { file, altText } = input;
      if (tenantId === null) throw new Error("no church resolved");
      if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
        throw new Error("That file is not a JPEG, PNG, WebP or AVIF image.");
      }
      if (file.size > MAX_IMAGE_BYTES) {
        throw new Error("That image is larger than 10 MB.");
      }
      if (altText.trim().length === 0) {
        throw new Error("Describe the image before uploading it.");
      }

      const path = buildObjectPath(
        STORAGE_BUCKETS.public,
        tenantId,
        "media",
        storageFileName(file.name),
      );
      const size = await imageSize(file);
      const uploaded = await supabase.storage
        .from(STORAGE_BUCKETS.public)
        .upload(path, file, { contentType: file.type, upsert: false });
      if (uploaded.error) throw uploaded.error;

      const { data, error } = await supabase.rpc("register_asset", {
        p_tenant_id: tenantId,
        p_storage_path: path,
        p_mime_type: file.type,
        p_byte_size: file.size,
        p_alt_text: altText.trim(),
        // The generated types make these optional, so a file whose size could not be read simply omits them.
        ...(size === null ? {} : { p_width: size.width, p_height: size.height }),
      });
      if (error !== null) {
        await supabase.storage.from(STORAGE_BUCKETS.public).remove([path]);
        failed(error);
      }
      return data as string;
    },
    onSuccess: invalidate,
  });
}

export function useUpdateAsset() {
  const supabase = useSupabase();
  const invalidate = useAssetInvalidation();
  return useMutation({
    mutationFn: async (input: { readonly assetId: string; readonly altText: string }) => {
      const { error } = await supabase.rpc("update_asset", {
        p_asset_id: input.assetId,
        p_alt_text: input.altText.trim(),
      });
      failed(error);
    },
    onSuccess: invalidate,
  });
}

/**
 * Removes the catalogue entry, then the file. In that order: if the file removal fails the church still sees the
 * image gone from the library, and an unreferenced file in the bucket is harmless. The reverse would leave a
 * catalogue entry pointing at nothing.
 */
export function useDeleteAsset() {
  const supabase = useSupabase();
  const invalidate = useAssetInvalidation();
  return useMutation({
    mutationFn: async (input: { readonly assetId: string; readonly storagePath: string }) => {
      const { error } = await supabase.rpc("delete_asset", { p_asset_id: input.assetId });
      failed(error);
      await supabase.storage.from(STORAGE_BUCKETS.public).remove([input.storagePath]);
    },
    onSuccess: invalidate,
  });
}
